import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Party, PartyCreate, PartyUpdate, PartyGroup, BUSINESS_TYPES, CLIENT_STATUSES, SOURCE_OPTIONS } from "@/app/types/party";
import { Site } from "@/app/types/site";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/app/store/authStore";
import { PartySelector } from "@/app/components/common/PartySelector";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

// PAN validation regex: 5 uppercase letters, 4 digits, 1 uppercase letter
const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
// TAN validation regex: 4 uppercase letters, 5 digits, 1 uppercase letter
const tanRegex = /^[A-Z]{4}[0-9]{5}[A-Z]{1}$/;

// Form schema using zod
const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  phone: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  gstin: z.string().optional().nullable(),
  gst_type: z.string().optional().nullable(),
  state: z.string().optional().nullable(),
  billing_address: z.string().optional().nullable(),
  shipping_address: z.string().optional().nullable(),
  has_separate_shipping: z.boolean().default(false),
  geo_location: z.string().optional().nullable(),
  opening_balance: z.union([z.string(), z.number()]).optional().nullable(),
  opening_balance_date: z.string().optional().nullable(),
  credit_limit_type: z.enum(["no_limit", "custom_limit"]).default("no_limit"),
  credit_limit_amount: z.union([z.string(), z.number()]).optional().nullable(),
  party_group_id: z.number().optional().nullable(),
  site_id: z.number().optional().nullable(),
  responsible_party_id: z.number().optional().nullable(),
  // Business Information fields
  trade_name: z.string().max(100, "Trade name too long").optional().nullable(),
  business_type: z.enum(['proprietorship', 'partnership', 'private_limited', 'llp', 'public_limited', 'individual', 'huf']).optional().nullable(),
  registration_number: z.string().max(50, "Registration number too long").optional().nullable(),
  date_of_incorporation: z.string().optional().nullable(),
  // Tax Identifiers
  pan: z.string().optional().nullable().refine(
    (val) => !val || panRegex.test(val),
    { message: "Invalid PAN format (e.g., ABCDE1234F)" }
  ),
  tan: z.string().optional().nullable().refine(
    (val) => !val || tanRegex.test(val),
    { message: "Invalid TAN format (e.g., ABCD12345E)" }
  ),
  // Additional Details
  city: z.string().max(100, "City name too long").optional().nullable(),
  pincode: z.string().max(6, "Pincode must be 6 digits").optional().nullable().refine(
    (val) => !val || /^[0-9]{6}$/.test(val),
    { message: "Pincode must be 6 digits" }
  ),
  contact_person: z.string().max(100, "Contact name too long").optional().nullable(),
  fiscal_year_start: z.string().optional().nullable(),
  fiscal_year_end: z.string().optional().nullable(),
  client_status: z.enum(['active', 'inactive', 'suspended']).optional().nullable(),
  source: z.enum(['Digital', 'BNI', 'Consultant']).optional().nullable(),
  source_party_id: z.number().optional().nullable(),
});

// GST types for select options
const gstTypes = [
  { value: "Registered", label: "Registered" },
  { value: "Unregistered/Consumer", label: "Unregistered/Consumer" },
];

// Indian states for select options
const indianStates = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

interface PartyFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: PartyCreate | PartyUpdate) => void;
  defaultValues?: Partial<Party>;
  isLoading?: boolean;
  mode: "create" | "edit";
  partyGroups: PartyGroup[];
  sites: Site[];
  defaultSiteId?: number | null;
  defaultGroupId?: number | null;
}

const PartyForm: React.FC<PartyFormProps> = ({
  open,
  onOpenChange,
  onSubmit,
  defaultValues,
  isLoading = false,
  mode,
  partyGroups,
  sites,
  defaultSiteId,
  defaultGroupId,
}) => {
  // Get current user from auth store
  const user = useAuthStore((state) => state.user);
  const isSuperAdmin = user?.username === 'superadmin';
  const isClientManager = user?.role?.name === 'client_manager';
  const managerPartyId = isClientManager ? user?.managed_parties?.[0]?.id : null;
  const managerPartyName = isClientManager ? user?.managed_parties?.[0]?.name : null;

  // Initialize the form
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: defaultValues?.name || "",
      phone: defaultValues?.phone || "",
      email: defaultValues?.email || "",
      gstin: defaultValues?.gstin || "",
      gst_type: defaultValues?.gst_type || "",
      state: defaultValues?.state || "Tamil Nadu",
      billing_address: defaultValues?.billing_address || "",
      shipping_address: defaultValues?.shipping_address || "",
      has_separate_shipping: defaultValues?.has_separate_shipping || false,
      geo_location: defaultValues?.geo_location || "",
      opening_balance: defaultValues?.opening_balance || "",
      opening_balance_date: defaultValues?.opening_balance_date || "",
      credit_limit_type: defaultValues?.credit_limit_type || "no_limit",
      credit_limit_amount: defaultValues?.credit_limit_amount || "",
      party_group_id: defaultValues?.party_group_id || (mode === "create" ? defaultGroupId : null) || null,
      site_id: defaultValues?.site_id || (mode === "create" ? defaultSiteId : null),
      responsible_party_id: defaultValues?.responsible_party_id || (mode === "create" && managerPartyId ? managerPartyId : null),
      // Business Information
      trade_name: defaultValues?.trade_name || "",
      business_type: defaultValues?.business_type || null,
      registration_number: defaultValues?.registration_number || "",
      date_of_incorporation: defaultValues?.date_of_incorporation || "",
      // Tax Identifiers
      pan: defaultValues?.pan || "",
      tan: defaultValues?.tan || "",
      // Additional Details
      city: defaultValues?.city || "",
      pincode: defaultValues?.pincode || "",
      contact_person: defaultValues?.contact_person || "",
      fiscal_year_start: defaultValues?.fiscal_year_start || "",
      fiscal_year_end: defaultValues?.fiscal_year_end || "",
      client_status: defaultValues?.client_status || "active",
      source: defaultValues?.source || null,
      source_party_id: defaultValues?.source_party_id || null,
    },
  });

  // Update shipping address when billing address changes if not separate
  const hasSeparateShipping = form.watch("has_separate_shipping");
  const billingAddress = form.watch("billing_address");

  // Set defaultGroupId when it resolves after mount (e.g. groups query loads late)
  useEffect(() => {
    if (mode === "create" && defaultGroupId && !form.getValues("party_group_id")) {
      form.setValue("party_group_id", defaultGroupId);
    }
  }, [mode, defaultGroupId, form]);

  // Clear source_party_id when source changes away from "Consultant"
  const sourceValue = form.watch("source");
  useEffect(() => {
    if (sourceValue !== "Consultant") {
      form.setValue("source_party_id", null);
    }
  }, [sourceValue, form]);

  // Handle form submission
  const handleSubmit = form.handleSubmit((data) => {
    onSubmit(data);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Add New Client" : "Edit Client"}
          </DialogTitle>
          <DialogDescription>
            Fill in the details to {mode === "create" ? "create a new" : "update this"} client.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Basic Information</h3>
              
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name*</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter party name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter phone number" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter email address" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="party_group_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client Group</FormLabel>
                      <Select
                        value={field.value?.toString() || "none"}
                        onValueChange={(value) => field.onChange(value === "none" ? null : parseInt(value))}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a group" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {partyGroups.map((group) => (
                            <SelectItem key={group.id} value={group.id.toString()}>
                              {group.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="site_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Site</FormLabel>
                      <Select
                        value={field.value?.toString() || "none"}
                        onValueChange={(value) => field.onChange(value === "none" ? null : parseInt(value))}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a site" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {sites.map((site) => (
                            <SelectItem key={site.id} value={site.id.toString()}>
                              {site.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="responsible_party_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Responsible Party</FormLabel>
                    {isClientManager ? (
                      <>
                        <Input
                          value={managerPartyName || user?.full_name || user?.username || 'Auto-assigned'}
                          disabled
                          className="bg-muted"
                        />
                        <FormDescription>
                          Auto-assigned to you as the responsible party
                        </FormDescription>
                      </>
                    ) : (
                      <>
                        <PartySelector
                          value={field.value ?? undefined}
                          onChange={(value) => field.onChange(value ?? null)}
                          placeholder="Select responsible employee..."
                          filterPartyGroup="Employee"
                          disabled={isLoading}
                        />
                        <FormDescription>
                          Assign an employee as the responsible party for this customer/vendor
                        </FormDescription>
                      </>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="source"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Source</FormLabel>
                    <Select
                      value={field.value || "not_specified"}
                      onValueChange={(value) => field.onChange(value === "not_specified" ? null : value)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="not_specified">Not Specified</SelectItem>
                        {SOURCE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      How the client was acquired
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.watch("source") === "Consultant" && (
                <FormField
                  control={form.control}
                  name="source_party_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Source Consultant</FormLabel>
                      <PartySelector
                        value={field.value ?? undefined}
                        onChange={(value) => field.onChange(value ?? null)}
                        placeholder="Select consultant..."
                        filterPartyGroup="Consultants"
                        disabled={isLoading}
                      />
                      <FormDescription>
                        The consultant who referred this client
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            {/* Business Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Business Information</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="trade_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Trade Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Trading/brand name" value={field.value || ""} />
                      </FormControl>
                      <FormDescription>Business trading or brand name</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="business_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Business Type</FormLabel>
                      <Select
                        value={field.value || "not_specified"}
                        onValueChange={(value) => field.onChange(value === "not_specified" ? null : value)}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select business type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="not_specified">Not Specified</SelectItem>
                          {BUSINESS_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="registration_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Registration Number</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="CIN/LLPIN" value={field.value || ""} />
                      </FormControl>
                      <FormDescription>Company CIN or LLP identification number</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="date_of_incorporation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date of Incorporation</FormLabel>
                      <FormControl>
                        <Input {...field} type="date" value={field.value || ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="client_status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client Status</FormLabel>
                    <Select
                      value={field.value || "active"}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CLIENT_STATUSES.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Tax Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Tax Information</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="pan"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>PAN</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="ABCDE1234F"
                          className="uppercase"
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                        />
                      </FormControl>
                      <FormDescription>Permanent Account Number</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="tan"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>TAN</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="ABCD12345E"
                          className="uppercase"
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                        />
                      </FormControl>
                      <FormDescription>Tax Deduction Account Number</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="gstin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>GSTIN</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter GSTIN" value={field.value || ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gst_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>GST Type</FormLabel>
                      <Select
                        value={field.value || "not_specified"}
                        onValueChange={(value) => field.onChange(value === "not_specified" ? null : value)}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select GST type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="not_specified">Not Specified</SelectItem>
                          {gstTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>State</FormLabel>
                    <Select
                      value={field.value || "not_specified"}
                      onValueChange={(value) => field.onChange(value === "not_specified" ? null : value)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="not_specified">Not Specified</SelectItem>
                        {indianStates.map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="fiscal_year_start"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fiscal Year Start</FormLabel>
                      <Select
                        value={field.value || "not_specified"}
                        onValueChange={(value) => field.onChange(value === "not_specified" ? null : value)}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select month" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="not_specified">Not Specified</SelectItem>
                          <SelectItem value="04-01">April (04-01)</SelectItem>
                          <SelectItem value="01-01">January (01-01)</SelectItem>
                          <SelectItem value="07-01">July (07-01)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>Start of financial year</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="fiscal_year_end"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fiscal Year End</FormLabel>
                      <Select
                        value={field.value || "not_specified"}
                        onValueChange={(value) => field.onChange(value === "not_specified" ? null : value)}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select month" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="not_specified">Not Specified</SelectItem>
                          <SelectItem value="03-31">March (03-31)</SelectItem>
                          <SelectItem value="12-31">December (12-31)</SelectItem>
                          <SelectItem value="06-30">June (06-30)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>End of financial year</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Address & Contact</h3>

              <FormField
                control={form.control}
                name="contact_person"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact Person</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Primary contact name" value={field.value || ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="billing_address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Billing Address</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Enter billing address"
                        rows={3}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter city" value={field.value || ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="pincode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pincode</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="6-digit pincode"
                          maxLength={6}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Shipping Address - Hidden for accounting firm */}
              {false && (
              <>
              <FormField
                control={form.control}
                name="has_separate_shipping"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Separate Shipping Address</FormLabel>
                      <FormDescription>
                        Check this if the shipping address is different from the billing address
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              {hasSeparateShipping && (
                <FormField
                  control={form.control}
                  name="shipping_address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Shipping Address</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Enter shipping address"
                          rows={3}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              </>
              )}

              <FormField
                control={form.control}
                name="geo_location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Geographic Location</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter location (e.g., 13.0827,80.2707)"
                      />
                    </FormControl>
                    <FormDescription>
                      Enter coordinates as latitude, longitude
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Financial Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Financial Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="opening_balance"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Opening Balance</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          disabled={mode === "edit"}
                        />
                      </FormControl>
                      {mode === "edit" && (
                        <FormDescription>Opening balance cannot be modified after creation</FormDescription>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="opening_balance_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>As of Date</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="date"
                          placeholder="Select date"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="credit_limit_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Credit Limit</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={!isSuperAdmin}
                    >
                      <FormControl>
                        <SelectTrigger disabled={!isSuperAdmin}>
                          <SelectValue placeholder="Select credit limit type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="no_limit">No Limit</SelectItem>
                        <SelectItem value="custom_limit">Custom Limit</SelectItem>
                      </SelectContent>
                    </Select>
                    {!isSuperAdmin && (
                      <FormDescription>Only superadmin can modify credit limits</FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.watch("credit_limit_type") === "custom_limit" && (
                <FormField
                  control={form.control}
                  name="credit_limit_amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Credit Limit Amount</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          step="0.01"
                          placeholder="Enter credit limit amount"
                          disabled={!isSuperAdmin}
                        />
                      </FormControl>
                      {!isSuperAdmin && (
                        <FormDescription>Only superadmin can modify credit limits</FormDescription>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {mode === "create" ? "Creating..." : "Updating..."}
                  </>
                ) : (
                  <>{mode === "create" ? "Create Party" : "Update Party"}</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default PartyForm; 